import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { shallow } from 'zustand/shallow';

import { LoadingSpinner } from '../../components/Molecules';
import { QuestionAnswerLanguageSelectPopup } from '../../components/Popups/QuestionAnswerLanguageSelectPopup';
import { GameAnalysisTemplate } from '../../components/Templates/Game';
import { getLanguageDetail } from '../../constants/languageDetails';
import { SpacePrivacyPolicy } from '../../constants/space';
import { SVGIconType } from '../../constants/svgIcon';
import { usePopup } from '../../context';
import useAbortControllerSignal from '../../hooks/useAbortControllerSignal';
import useFetchList, { FetchListFn } from '../../hooks/useFetchList';
import useRedirectEntry from '../../hooks/useRedirectEntry';
import useRedirectHome from '../../hooks/useRedirectHome';
import { TranslationLocale } from '../../i18n';
import { Locale, TtsLocale } from '../../models/Base';
import { GameType } from '../../models/GameStore';
import { useFileStore } from '../../stores/FileStore';
import { useGameStore, useGameStoreListField } from '../../stores/GameStore';
import {
  useRecordStore,
  useRecordStoreListField,
} from '../../stores/RecordStore';
import { useSessionStore } from '../../stores/SessionStore';
import { useSpaceStore } from '../../stores/SpaceStore';
import { arrayEqual } from '../../utils/array';
import { getUiLocale } from '../../utils/i18n';

type State = {
  isUseTranslate: boolean;
  tempLanguage: TtsLocale | undefined;
  originalLanguage: TtsLocale | undefined;
  translatedLanguage: TtsLocale | undefined;
  originalAudioTts: string | null | undefined;
  translatedAudioTts: string | null | undefined;
  translationResult: string | undefined;
  isHandwriting: boolean;
  isLoadingTotal: number;
};

const { fetchGame } = useGameStore.getState();
const { getTtsAudios, getTranslationText } = useFileStore.getState();

const GameAnalysisScreen: React.FC = () => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { showPopup, hidePopup } = usePopup();
  const { classroomId, gameSetId, gameId } = useParams();
  const signal = useAbortControllerSignal();
  const query = new URLSearchParams(location.search);
  const mode = query.get('mode');
  const locationState = location.state as {
    isUseTranslate: boolean;
    translatedLanguage: TtsLocale | undefined;
  } | null;

  const [state, setState] = useState<State>({
    isUseTranslate: false,
    tempLanguage: undefined,
    originalLanguage: undefined,
    translatedLanguage: undefined,
    originalAudioTts: undefined,
    translatedAudioTts: undefined,
    translationResult: undefined,
    isHandwriting: false,
    isLoadingTotal: 0,
  });

  useEffect(() => {
    if (locationState) {
      setState((s) => ({
        ...s,
        isUseTranslate: locationState.isUseTranslate,
        translatedLanguage: locationState.translatedLanguage,
      }));
    }
  }, [locationState]);

  // MARK: Login Check
  const activeUserId = useSessionStore((store) => store.activeUserId);
  useRedirectEntry();

  const { classroom, classroomMembership } = useSpaceStore((store) => {
    const space = store.getSpace(classroomId ?? '');
    const membership = store.getActiveUserMembership(classroomId ?? '');
    return {
      classroom: space,
      classroomMembership: membership,
    };
  }, shallow);

  // MARK: Club Access Check
  const isAllowToAccessClub =
    (classroom?.privacyPolicy === SpacePrivacyPolicy.Public ||
      (classroom?.privacyPolicy === SpacePrivacyPolicy.PrivateAndVisible &&
        classroomMembership?.isInGroup)) ??
    false;

  useRedirectHome(!isAllowToAccessClub);

  const gameRecordFieldName = `${classroomId}_${gameSetId}`;
  const gameRecordField = useRecordStoreListField(
    'gameRecords',
    gameRecordFieldName,
  );

  const fetchGameRecords = useCallback<FetchListFn>(
    (cursor) =>
      useRecordStore.getState().fetchGameRecords(
        {
          fieldName: gameRecordFieldName,
          classroomId: classroomId ?? '',
          playedBy: activeUserId ?? '',
          gameSetId: gameSetId ?? '',
          gameId: gameId ?? '',
        },
        cursor,
        { headers: { 'cache-control': 'no-cache' } },
      ),
    [activeUserId, classroomId, gameId, gameRecordFieldName, gameSetId],
  );

  const { fetchAll: fetchAllGameRecords } = useFetchList(
    gameRecordField,
    fetchGameRecords,
  );

  useEffect(() => {
    // if (gameItems) {
    setState((s) => ({ ...s, isLoadingTotal: s.isLoadingTotal + 1 }));
    fetchAllGameRecords()
      .then(() => {
        setState((s) => ({ ...s, isLoadingTotal: s.isLoadingTotal - 1 }));
      })
      .catch((error) => {
        console.error(error);
      });
    // }
  }, [fetchAllGameRecords]);

  const allGameRecords = useRecordStore((store) => {
    return store.getGameRecords(gameRecordFieldName);
  }, arrayEqual(shallow));
  // Find the first record with a defined score and type not equal to Assisted
  const latestRecord = allGameRecords.find(
    (record) =>
      record.isAssisted === (mode === 'tutorial') &&
      record.type !== undefined &&
      record.completed,
  );

  const gamesFieldName = `${gameId}`;
  useGameStoreListField('games', gamesFieldName);

  useEffect(() => {
    if (latestRecord) {
      fetchGame({
        fieldName: gamesFieldName,
        gameCollectionId: latestRecord.gameCollectionId,
        gameId: latestRecord.gameId,
        hash: latestRecord.gameHash,
      }).catch(console.error);
    }
  }, [gamesFieldName, latestRecord]);

  const game = useGameStore((store) => {
    return store.getGame(gameId ?? '');
  });

  useEffect(() => {
    if (game) {
      const locale =
        game.i18nGameData.resources[game.i18nGameData.defaultLocale]
          ?.variants[0]?.answer.locale;
      const gameType = game.gameType;
      const isHandwriting = gameType === GameType.FingerWriting;
      setState((s) => ({
        ...s,
        originalLanguage: locale,
        isHandwriting: isHandwriting,
      }));
    }
  }, [game]);

  // const onClickBack = () => {
  //   navigate(`/club/${classroomId ?? ''}/journey/${gameSetId}`);
  // };
  const onClickPlayGame = () => {
    navigate(`/club/${classroomId}/journey/${gameSetId}/game/${gameId}`, {
      state: { from: 'analysisPage' },
    });
  };

  const onClickMemoryHelper = () => {
    navigate(`/club/${classroomId}/journey/${gameSetId}/game/${gameId}/helper`);
  };

  const onGetTtsAudio = useCallback(
    async (text: string, locale: TtsLocale, isTranslateAudio: boolean) => {
      if (text) {
        setState((s) => ({ ...s, isLoadingTotal: s.isLoadingTotal + 1 }));
        const audioWithUrl = await getTtsAudios([text], locale, signal);
        setState((s) => ({ ...s, isLoadingTotal: s.isLoadingTotal - 1 }));
        if (audioWithUrl) {
          const url = audioWithUrl[text]?.url;
          if (url) {
            if (isTranslateAudio) {
              setState((s) => ({ ...s, translatedAudioTts: url }));
            } else {
              setState((s) => ({ ...s, originalAudioTts: url }));
            }
          }
        }
      }
    },
    [signal],
  );

  useEffect(() => {
    if (latestRecord && state.originalLanguage) {
      const words = latestRecord.evaluation?.inApp.map(
        (evaluation) => evaluation.word,
      );
      const text = words?.join('');
      if (text) {
        onGetTtsAudio(text, state.originalLanguage, false).catch(console.error);
      }
    }
  }, [latestRecord, onGetTtsAudio, state.originalLanguage]);

  useEffect(() => {
    setState((s) => ({
      ...s,
      translatedLanguage:
        s.translatedLanguage === undefined
          ? getUiLocale(i18n.language as TranslationLocale)
          : s.translatedLanguage,
    }));
  }, [i18n.language]);

  const onSwitchTranslate = useCallback(() => {
    setState((s) => ({ ...s, isUseTranslate: !s.isUseTranslate }));
  }, []);

  const onAutoTranslate = useCallback(async () => {
    if (
      latestRecord &&
      state.isUseTranslate &&
      state.translatedLanguage &&
      state.originalLanguage
    ) {
      const words = latestRecord.evaluation?.inApp.map(
        (evaluation) => evaluation.word,
      );
      if (words) {
        setState((s) => ({ ...s, isLoadingTotal: s.isLoadingTotal + 1 }));
        const text = await getTranslationText(
          [words.join('')],
          state.originalLanguage,
          state.translatedLanguage,
        );

        const translationText = text?.join('');

        if (translationText) {
          await onGetTtsAudio(translationText, state.translatedLanguage, true);
        }

        setState((s) => ({ ...s, isLoadingTotal: s.isLoadingTotal - 1 }));
        if (translationText) {
          setState((s) => ({
            ...s,
            translationResult: translationText,
          }));
        }
      }
    }
  }, [
    latestRecord,
    onGetTtsAudio,
    state.isUseTranslate,
    state.originalLanguage,
    state.translatedLanguage,
  ]);

  useEffect(() => {
    if (
      state.isUseTranslate &&
      state.translatedLanguage &&
      state.originalLanguage
    ) {
      onAutoTranslate().catch(console.error);
    }
  }, [
    onAutoTranslate,
    state.isUseTranslate,
    state.originalLanguage,
    state.translatedLanguage,
  ]);

  const handleLanguagePopup = useCallback(
    (selectedLocale: TtsLocale) => {
      showPopup(
        <QuestionAnswerLanguageSelectPopup
          title={'Translation'}
          titleIcon={SVGIconType.Translation}
          languageList={undefined}
          selectedLanguage={selectedLocale}
          recentLanguage={undefined}
          onClickOK={() => {
            setState((s) => ({ ...s, translatedLanguage: s.tempLanguage }));
            hidePopup();
          }}
          onCancel={hidePopup}
          onSelectLanguage={(language: TtsLocale) => {
            setState((s) => ({ ...s, tempLanguage: language }));
            handleLanguagePopup(language); // Re-show popup with updated locale
          }}
        />,
      );
    },
    [hidePopup, showPopup],
  );

  const onClickLanguage = useCallback(() => {
    if (state.translatedLanguage) {
      setState((s) => ({ ...s, tempLanguage: s.translatedLanguage }));
      handleLanguagePopup(state.translatedLanguage);
    }
  }, [handleLanguagePopup, state.translatedLanguage]);

  return (
    <>
      <GameAnalysisTemplate
        latestRecord={latestRecord}
        answerLocale={state.originalLanguage ?? Locale.US}
        isUseTranslate={state.isUseTranslate}
        originalLanguage={
          getLanguageDetail(state.originalLanguage)?.displayName ?? ''
        }
        translatedLanguage={
          getLanguageDetail(state.translatedLanguage)?.displayName ?? ''
        }
        originalAudioTts={state.originalAudioTts}
        translatedAudioTts={state.translatedAudioTts}
        translationResult={state.translationResult}
        isHandwriting={state.isHandwriting}
        onClickPlayGame={onClickPlayGame}
        onSwitchTranslate={onSwitchTranslate}
        onClickLanguage={onClickLanguage}
        onClickMemoryHelper={onClickMemoryHelper}
      />
      {state.isLoadingTotal > 0 && (
        <LoadingSpinner loading={!!state.isLoadingTotal} />
      )}
    </>
  );
};

export default GameAnalysisScreen;
